import { Box, Card, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { Dispatch, SetStateAction } from 'react';

const QuotesCountCard: React.FC<{
	label: string;
	count: number;
	id: string;
	tilesCount: number;
	cardFilter: string;
	setCardFilter: Dispatch<SetStateAction<string>>;
	setLast30Days: Dispatch<SetStateAction<boolean>>;
	last30Days: boolean;
	selectedCardStatus: string;
	setSelectedCardStatus: Dispatch<SetStateAction<string>>;
}> = ({
	label,
	count,
	tilesCount,
	setCardFilter,
	setLast30Days,
	last30Days,
	selectedCardStatus,
	setSelectedCardStatus,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	// Map card labels to the filter values used by the API (not status values)
	const getFilterValue = (cardLabel: string) => {
		switch (cardLabel) {
			case 'Quotes Started':
				return 'Started';
			case 'Quotes Completed':
				return 'Completed';
			case 'Proposal Sent':
				return 'Proposal Sent';
			case 'Proposal Signed':
				return 'Proposal Signed';
			default:
				return '';
		}
	};

	const filterValue = getFilterValue(label);
	// Card is selected if this card's status is currently selected AND we're filtering by last 30 days
	const isSelected = selectedCardStatus === filterValue && last30Days;

	// Use theme palette and typography
	const cardBg = isSelected
		? theme.palette.primary.main
		: theme.palette.background.paper;
	const cardColor = isSelected
		? theme.palette.background.paper
		: theme.palette.text.primary;

	return (
		<Card
			onClick={
				count === 0
					? undefined
					: () => {
							if (isSelected) {
								// Clear card filters
								setCardFilter('');
								setSelectedCardStatus('');
								setLast30Days(false);
							} else {
								// Set card-specific status and enable 30-day filter
								setSelectedCardStatus(filterValue);
								setLast30Days(true);
								setCardFilter('');
							}
						}
			}
			elevation={isSelected ? 3 : 1}
			sx={{
				width: `calc(95% / ${tilesCount})`,
				p: 1,
				cursor: count === 0 ? 'not-allowed' : 'pointer',
				bgcolor: cardBg,
				color: cardColor,
				transition: theme.transitions.create(
					['background-color', 'box-shadow'],
					{
						duration: theme.transitions.duration.short,
					}
				),
				opacity: count === 0 ? 0.6 : 1,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: isMobile ? 'column-reverse' : 'row',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
				}}
			>
				<Typography
					sx={{
						fontWeight: 700,
						fontSize: theme.typography['4xl'],
						color: isSelected ? 'inherit' : theme.palette.text.primary,
					}}
				>
					{count}
				</Typography>
				<Typography
					sx={{
						fontSize: theme.typography['2xs'],
						color: isSelected ? 'inherit' : theme.palette.text.disabled,
					}}
				>
					Last 30 days
				</Typography>
			</Box>
			<Typography
				sx={{
					fontSize: theme.typography.lg,
					color: isSelected ? 'inherit' : theme.palette.text.primary,
				}}
			>
				{label.split(' ')[0]} <b>{label.split(' ')[1]}</b>
			</Typography>
		</Card>
	);
};

export default QuotesCountCard;
